import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

export interface HeaderContent {
  preview: unknown;
  fileName?: string;
  rawFile?: unknown;
}

export interface TemplateHeader {
  text: string;
  name: string;
  format: 'TEXT' | 'VIDEO' | 'IMAGE' | 'DOCUMENT' | string;
  content: HeaderContent;
}

export interface TemplateButton {
  text: string;
  type: 'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER' | string;
  url?: string;
  urlType?: 'STATIC' | 'DYNAMIC' | 'TRACKING' | string;
  phone_number?: string;
  savedUrl?: string;
  ttl_minutes?: number;
}

export interface TemplateURLButton {
  id: string;
  templateId: string;
  buttonType: 'main' | 'carousel';
  buttonIndex: number;
  cardIndex?: number;
  url: string;
  code?: string;
}

export interface CarouselCard {
  header: TemplateHeader;
  body: string;
  bodyExample: string[];
  footer: string;
  buttons: TemplateButton[];
  buttonType: 'CTA' | 'QUICK_REPLY' | string;
}

export interface CarouselHeaderData {
  id: string;
  templateId: string;
  cardIndex: number;
  mediaUrl: string;
}

export interface WhatsappTemplateSlice {
  id: string;
  channelId: string;
  name: string;
  header: TemplateHeader;
  body: string;
  bodyExample: string[];
  buttonType: 'CTA' | 'QUICK_REPLY' | string;
  buttons: TemplateButton[];
  language: string;
  footer: string;
  category: 'MARKETING' | 'AUTHENTICATION' | 'UTILITY' | string;
  status: 'APPROVED' | 'PENDING' | 'REJECTED' | string;
  add_security_recommendation: boolean;
  headerMediaUrl?: string;
  carousel?: CarouselCard[];
  carouselHeader: CarouselHeaderData[];
  templateButton: TemplateURLButton[];
}

// Initial state for the slice
export const WhatsappTemplateInitialState: {
  template: WhatsappTemplateSlice;
  readonly: false;
  action: 'add' | 'edit';
} = {
  template: {
    id: '',
    channelId: '',
    name: '',
    header: {
      text: '',
      name: '',
      format: '',
      content: { preview: '' },
    },
    body: '',
    bodyExample: [],
    buttonType: '',
    buttons: [],
    language: 'id',
    footer: '',
    category: 'MARKETING',
    status: '',
    add_security_recommendation: false,
    headerMediaUrl: '',
    carousel: [],
    carouselHeader: [],
    templateButton: [],
  },
  readonly: false,
  action: 'add',
};

export const whatsappTemplateSlice = createSlice({
  name: 'whatsappTemplate',
  initialState: WhatsappTemplateInitialState,
  reducers: {
    setChannelId: (state, action) => {
      state.template.channelId = action.payload;
    },
    resetTemplate: (state) => {
      state.template = WhatsappTemplateInitialState.template;
    },
    setTemplate: (state, action: PayloadAction<WhatsappTemplateSlice>) => {
      state.template = action.payload;
    },
    setReadonly: (state, action) => {
      state.readonly = action.payload;
    },
    setAction: (state, action) => {
      state.action = action.payload;
    },
  },
});

export const { resetTemplate, setTemplate, setReadonly, setAction } =
  whatsappTemplateSlice.actions;
